import { forwardRef } from "react";
import Tooltip from "../../tooltip/Tooltip";
import "./_text-area.scss";

/**
 * Pure-UI multiline text input. Same contract as TextInput (onChange
 * receives the raw value, not the DOM event) so it's a drop-in sibling for
 * the same form-engine wiring. No validation logic here - that's the
 * caller's job (e.g. react-hook-form).
 */
const TextArea = forwardRef(function TextArea(
  {
    id,
    label,
    value,
    onChange,
    onBlur,
    error,
    disabled = false,
    required = false,
    placeholder = "",
    rows = 4,
    tooltip,
    className = "",
    ...rest
  },
  ref
) {
  // Matches TextInput's required-asterisk contract (see its own doc
  // comment) - both fields now render their label on its own line above
  // the box, so they need to agree on what goes in that line too.
  const displayLabel = required && label ? `${label} *` : label;

  return (
    <Tooltip content={tooltip} disabled={!tooltip} className="text-area-tooltip-anchor">
      <div
        className={`text-area ${error ? "text-area--error" : ""} ${
          disabled ? "text-area--disabled" : ""
        } ${className}`}
      >
        {label && (
          <label className="text-area__label" htmlFor={id}>
            {displayLabel}
          </label>
        )}
        <div className="text-area__field-wrapper">
          <textarea
            ref={ref}
            id={id}
            className="text-area__field"
            value={value ?? ""}
            placeholder={placeholder}
            rows={rows}
            readOnly={disabled}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : undefined}
            required={required}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
            {...rest}
          />
        </div>
        {error && <p className="text-area__error">{error}</p>}
      </div>
    </Tooltip>
  );
});

export default TextArea;
